import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectIsAuthenticated,
  selectCurrentUser,
  getCurrentUser,
} from '../../store/slices/authSlice';
import { usePermission } from '../../hooks/usePermission';
import { getRolesForPath } from '../../config/routePermissions';
import Unauthorized from './Unauthorized';
import Loader from '../Common/Loader';

const ProtectedRoute = ({
  children,
  requiredPermissions = [],
  requiredRoles = [],
  requireAllPermissions = true,
  requireAllRoles = true,
  redirectTo,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();

  const isAuthenticated = useSelector(selectIsAuthenticated);
  const currentUser     = useSelector(selectCurrentUser);

  const {
    hasAllPermissions,
    hasAnyPermission,
    hasAllRoles,
    hasAnyRole,
    isSuperAdmin,
    canAccessRoles,
  } = usePermission();

  /* 1. Fetch user if authenticated but not loaded */
  useEffect(() => {
    if (isAuthenticated && !currentUser) {
      dispatch(getCurrentUser());
    }
  }, [isAuthenticated, currentUser, dispatch]);

  /* 2. Not logged in → login */
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  /* 3. Wait for user data */
  if (!currentUser) {
    return <Loader />;
  }

  /* 4. Super admin bypass */
  if (isSuperAdmin) {
    return children;
  }

  /* 5. Resolve requirements — props win, else config */
  const effectivePermissions = requiredPermissions;
  const effectiveRoles =
    requiredRoles.length > 0
      ? requiredRoles
      : (getRolesForPath(location.pathname) || []);

  /* 6. Permission check */
  if (effectivePermissions.length > 0) {
    const ok = requireAllPermissions
      ? hasAllPermissions(effectivePermissions)
      : hasAnyPermission(effectivePermissions);
    if (!ok) return renderDenial(redirectTo, location);
  }

  /* 7. Role check */
  if (effectiveRoles.length > 0) {
    const ok =
      requiredRoles.length > 0
        ? (requireAllRoles
            ? hasAllRoles(effectiveRoles)
            : hasAnyRole(effectiveRoles))
        : canAccessRoles(effectiveRoles);
    if (!ok) return renderDenial(redirectTo, location);
  }

  return children;
};

/* Render 403 or redirect */
function renderDenial(redirectTo, location) {
  if (redirectTo) {
    return <Navigate to={redirectTo} replace state={{ denied: location.pathname }} />;
  }
  return <Unauthorized />;
}

export default ProtectedRoute;